import React, { useState, useEffect, useMemo, forwardRef, useCallback } from 'react';
import FormInput from './FormInput'; // Assuming FormInput is in the same directory
import { FaPlus, FaTimes, FaSpinner, FaArrowRight, FaArrowLeft } from 'react-icons/fa';

const NewRecordForm = forwardRef(({
  onCreate,
  onCancel,
  columnProps = [],
  inputTypes = {},
  lookupOptions = {},
  isLoadingLookups = false,
  theme = {},
  isProcessing = false,
  initialData: propInitialData = {},
  creationNoun = "Elemento",
  formLayoutConfig,
  customFormValidation, // **NUOVO**: Aggiungi questa prop
  onFieldChange, // NUOVO: Callback per side-effects al cambio di un campo
  isFieldVisible,
}, ref) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const themeColor = theme?.themeColor || 'primary';

  const formVisibleColumnProps = useMemo(() => {
    return columnProps.filter(col =>
      col &&
      (col.visibility === 1 || col.visibility === 2) &&
      !col.isPrimaryKey &&
      (!isFieldVisible || isFieldVisible(col, formData, 'create'))
    );
  }, [columnProps, formData, isFieldVisible]);

  const {
    generalFields, 
    parameterFields 
  } = useMemo(() => {
    const gen = [];
    const params = {}; 

    formVisibleColumnProps.forEach(col => {
      const match = col.field.match(/^FAG_(NomeParametro|TipoParametro)(In|Out)(\d+)$/);
      if (match) {
        const type = match[1]; 
        const direction = match[2]; 
        const number = match[3]; 
        if (!params[number]) {
          params[number] = { number };
        }
        if (type === 'NomeParametro') {
          params[number][direction === 'In' ? 'nomeIn' : 'nomeOut'] = col;
        } else if (type === 'TipoParametro') {
          params[number][direction === 'In' ? 'tipoIn' : 'tipoOut'] = col;
        }
      } else {
        gen.push(col);
      }
    });
    
    const orderedParamFields = Object.keys(params)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map(num => params[num]);

    return { generalFields: gen, parameterFields: orderedParamFields };
  }, [formVisibleColumnProps]);

  useEffect(() => {
    const initialFormState = {};
    
    // **FIX: Escludi i campi ID, IdRif e campi di audit/sistema automaticamente**
    columnProps.forEach(col => {
      // Salta i campi che:
      // 1. Sono primary key
      // 2. Hanno "Id" o "IdRif" dopo i primi 4 caratteri (es: CAM_Id_Campi, CAM_IdRif_Campi)
      //    MA NON escludere le Foreign Key come CAM_Id_Tabelle, CAM_Id_Oggetti, ATT_Id_Oggetti
      // 3. Sono campi di audit/sistema
      if (col.isPrimaryKey) {
        return;
      }
      
      // **MIGLIORIA CRITICA**: Se abbiamo dati iniziali dalla duplicazione, usa SEMPRE quei valori
      // Questo permette di preservare i valori dei lookup anche per i campi FK
      if (propInitialData && propInitialData.hasOwnProperty(col.field)) {

        initialFormState[col.field] = propInitialData[col.field];
        return; // Usa il valore dalla duplicazione, salta la logica di filtro
      }
      
      // Solo per i nuovi record (non duplicati), applica il filtro dei campi Id/IdRif
      if (col.field && col.field.length > 4) {
        const afterPrefix = col.field.substring(4);
        
        // **Escludi campi di audit e sistema**
        const auditFields = [
          'StatiRecord',
          'StatoRecord',        // ✅ AGGIUNGI QUESTO per escludere RIC_StatoRecord
          'InizioValidita', 
          'FineValidita',
          'UtenteInserimento',
          'DataOraInserimento',
          'DataOraModifica', 
          'TerminaleInserimento',
          'TerminaleModifica',
          'UtenteModifica',
          'Utente',     // AGGIUNTO: Campo audit mancante
          'Terminale',  // AGGIUNTO: Campo audit mancante
          'Timestamp'
        ];
        
        if (auditFields.some(auditField => afterPrefix.startsWith(auditField))) {
          return; // Salta i campi di audit
        }
        
        // **MIGLIORIA**: Per i nuovi record, consenti comunque le FK standard
        const allowedForeignKeys = [
          // Standard Foreign Keys che dovrebbero essere inizializzabili
          'CAM_Id_Tabelle',     // Campi - Tabella
          'CAM_Id_Oggetti',     // Campi - Oggetto
          'CAM_Id_TipiCampi',   // Campi - Tipo Campo
          'CAM_Extra_Tabelle',  // Campi - Tabelle Extra
          'ATT_Id_Oggetti',     // Attributi - Oggetto
          'ATT_Super_Oggetti',  // Attributi - Super Oggetto
          'ATT_TipiCampi',      // Attributi - Tipo Campo
          'OGG_Id_Moduli',      // Oggetti - Modulo
          'OGG_Id_Tabelle',     // Oggetti - Tabella
          'TAB_Id_Moduli',      // Tabelle - Modulo
          'MAC_Id_Moduli',      // MacroFunzioni - Modulo
          'MAC_Id_Tabelle',     // MacroFunzioni - Tabella
          'TEM_IdRichiedente_Utenti', // Test Modifiche - Richiedente
          'TEM_IdEsecutore_Utenti',   // Test Modifiche - Esecutore
          'RIC_Id_Prodotti',    // ✅ AGGIUNGI QUESTO - Richieste - Prodotto
          'RIC_Id_TipiUrgenza', // ✅ AGGIUNGI QUESTO - Richieste - Tipo Urgenza
          'DOW_Id_MacroFunzioni', // AGGIUNTO: Documentazione Web - Macro Funzione
          // Aggiungi altre FK complete se necessario
        ];
        
        if (afterPrefix.startsWith('Id_') || afterPrefix.startsWith('IdRif_')) {
          // Se è una Foreign Key consentita (nome completo), NON escluderla
          if (allowedForeignKeys.includes(col.field)) {
            // Non fare nulla, continua con l'inizializzazione
          } else {
            return; // Salta questi campi
          }
        }
      }
      
      // **INIZIALIZZAZIONE STANDARD**: Inizializza con valori di default appropriati
      if (inputTypes[col.field] === 'checkbox' || col.type === 'boolean') {
        // **FIX: Gestione migliorata per campi booleani**
        initialFormState[col.field] = false; // Predefinito sempre false per booleani
      } else if (inputTypes[col.field] === 'select') {
        // **FIX**: Default 0 per tutti i campi select (FK numeriche)
        initialFormState[col.field] = 0;
      } else {
        initialFormState[col.field] = '';
      }
    });
    

    setFormData(initialFormState);
  }, [columnProps, inputTypes, propInitialData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // Se c'è un callback onFieldChange, calcola eventuali side-effects
      if (onFieldChange) {
        const sideEffects = onFieldChange(field, value, updated);
        if (sideEffects && typeof sideEffects === 'object') {
          Object.assign(updated, sideEffects);
        }
      }
      return updated;
    });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // **VALIDAZIONE BUILT-IN per Test Modifiche**: Controlla utenti duplicati
  const validateTestModificheUsers = useCallback((formData, newErrors) => {
    // Se ci sono campi TEM_IdRichiedente_Utenti e TEM_IdEsecutore_Utenti, controllali
    if (formData.TEM_IdRichiedente_Utenti && formData.TEM_IdEsecutore_Utenti) {
      if (formData.TEM_IdRichiedente_Utenti === formData.TEM_IdEsecutore_Utenti) {
        const errorMessage = "L'utente richiedente e l'utente esecutore devono essere diversi";
        newErrors.TEM_IdRichiedente_Utenti = errorMessage;
        newErrors.TEM_IdEsecutore_Utenti = errorMessage;
        

      }
    }
    return newErrors;
  }, []);

  const validateForm = () => {
    const newErrors = {};
    formVisibleColumnProps.forEach(col => {
      if (col.required && !col.readOnly) {
        const value = formData[col.field];
        if (value === null || value === undefined || String(value).trim() === '') {
          newErrors[col.field] = `${col.label || col.field} è obbligatorio.`;
        }
      }
    });
    
    // **VALIDAZIONE BUILT-IN**: Applica sempre la validazione per Test Modifiche

    validateTestModificheUsers(formData, newErrors);
    
    // **VALIDAZIONE PERSONALIZZATA**: Applica validazione personalizzata se fornita
    if (customFormValidation && typeof customFormValidation === 'function') {

      const customErrors = customFormValidation(formData, newErrors);
      Object.assign(newErrors, customErrors);
    }
    

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    
    if (validateForm()) {

      
      // **FIX: Filtra i dati prima dell'invio per escludere campi ID, IdRif e audit**
      const submissionData = {};
      
      // Copia solo i campi che non sono ID/IdRif, primary key o campi di audit
      Object.keys(formData).forEach(fieldName => {

        
        const col = columnProps.find(c => c.field === fieldName);

        // Un campo nascosto dal contesto non deve essere inviato al backend.
        if (isFieldVisible && col && !isFieldVisible(col, formData, 'create')) {
          return;
        }
        
        // Salta i campi primary key
        if (col && col.isPrimaryKey) {
          console.log(`[NewRecordForm] Skipping primary key field: ${fieldName}`);
          return;
        }
        
        // Salta i campi con "Id" o "IdRif" dopo i primi 4 caratteri
        if (fieldName && fieldName.length > 4) {
          const afterPrefix = fieldName.substring(4);
          
          // **MIGLIORIA**: Per i nuovi record, consenti comunque le FK standard
          const allowedForeignKeys = [
            'CAM_Id_Tabelle',
            'CAM_Id_Oggetti',
            'CAM_Id_TipiCampi',
            'CAM_Extra_Tabelle',
            'ATT_Id_Oggetti',
            'ATT_Super_Oggetti',
            'ATT_TipiCampi',
            'OGG_Id_Moduli',      // <- QUESTO DOVREBBE ESSERE INCLUSO
            'OGG_Id_Tabelle',
            'TAB_Id_Moduli',
            'MAC_Id_Moduli',
            'MAC_Id_Tabelle',
            'FAG_Id_MacroFunzione', // AGGIUNTO: Per collegare funzioni aggiuntive alla macro funzione
            'TEM_Id_MacroFunzioni', // AGGIUNTO: Per collegare test modifiche alla macro funzione // AGGIUNTO: Per collegare tabelle alla macro funzione
            'TEM_IdRichiedente_Utenti',
            'TEM_IdEsecutore_Utenti',
            'RIC_Id_Prodotti',
            'RIC_Id_TipiUrgenza',
            'DOW_Id_MacroFunzioni', // AGGIUNTO: Per collegare documentazione web alla macro funzione
          ];
          
          if (afterPrefix.startsWith('Id_') || afterPrefix.startsWith('IdRif_')) {
            if (!allowedForeignKeys.includes(fieldName)) {
      
              return;
            } else {
               // DEBUG
            }
          }
        }
        
        // **FIX: Gestione speciale per campi booleani e selects**
        let fieldValue = formData[fieldName];
        
         // DEBUG
        
        // Converti stringhe vuote in null per campi numerici
        if ((col?.type === 'number' || inputTypes[fieldName] === 'number') && (fieldValue === '' || fieldValue === undefined)) {
          fieldValue = null;
        }

        // Includi il campo con il valore processato
        submissionData[fieldName] = fieldValue;
      });
      
       // Questo dovrebbe mostrare OGG_Id_Moduli
      onCreate(submissionData);
    } else {
      console.log("[NewRecordForm] Validation failed! Errors:", errors);
    }
  };

  const renderFormInput = (col) => {
    if (!col) return <div className="h-[76px]"></div>; 
    const inputType = inputTypes[col.field] || col.type || 'text';
     if (col.field === 'FAG_Id_MacroFunzione' && col.readOnly) {
      return (
        <div>
          <label htmlFor={col.field} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {col.label || col.field}
          </label>
          <input
            type="text"
            id={col.field}
            value={formData[col.field] || ''}
            readOnly
            className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm sm:text-sm bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
          />
        </div>
      );
    }
    return (
      <FormInput
        key={col.field}
        field={col.field}
        label={col.label || col.field}
        value={formData[col.field]}
        onChange={handleInputChange}
        inputType={inputType}
        lookupOptions={lookupOptions[col.lookupName || col.field]}
        isLoadingLookups={isLoadingLookups}
        placeholder={col.placeholder || ''}
        disabled={isProcessing || col.readOnly}
        error={errors[col.field]}
        themeColor={themeColor}
        required={col.required}
        isPrimaryKey={col.isPrimaryKey}
        readOnly={col.readOnly}
        showForeignKeyInfo={col.field.endsWith('Id_Tabelle')} // AGGIUNTA: Stessa logica dell'EditRecordForm
      />
    );
  };
  
  const displayableGeneralFields = generalFields.filter(col => !col.field.startsWith('FAG_NomeParametro') && !col.field.startsWith('FAG_TipoParametro'));

  // MODIFICATO: Aggiorna renderColumnSection per gestire array di campi affiancati
  const renderColumnSection = useCallback((section, themeColor) => {
    if (section.layout !== 'twoColumns') {
      // Layout normale per sezioni standard
      return (
        <div className="space-y-4">
          {section.fields.map(field => {
            // Gestisci sia singoli campi che array di campi
            if (Array.isArray(field)) {
              // Array di campi da affiancare
              return (
                <div key={field.join('-')} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {field.map(fieldName => {
                    const col = formVisibleColumnProps.find(c => c.field === fieldName);
                    if (!col) return null;
                    return (
                      <div key={fieldName}>
                        {renderFormInput(col)}
                      </div>
                    );
                  })}
                </div>
              );
            } else {
              // Campo singolo
              const col = formVisibleColumnProps.find(c => c.field === field);
              if (!col) return null;
              return renderFormInput(col);
            }
          })}
        </div>
      );
    }

    // Layout a due colonne per i parametri
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {section.columns.map((column, columnIndex) => (
          <div key={columnIndex} className="space-y-4">
            {/* Header della colonna */}
            <div className={`flex items-center space-x-2 p-3 bg-${themeColor}-50 dark:bg-${themeColor}-900/20 rounded-lg border border-${themeColor}-200 dark:border-${themeColor}-700`}>
              {column.icon === 'FaArrowRight' && <FaArrowRight className={`h-4 w-4 text-${themeColor}-600 dark:text-${themeColor}-400`} />}
              {column.icon === 'FaArrowLeft' && <FaArrowLeft className={`h-4 w-4 text-${themeColor}-600 dark:text-${themeColor}-400`} />}
              <h4 className={`text-sm font-semibold text-${themeColor}-800 dark:text-${themeColor}-200`}>
                {column.title}
              </h4>
            </div>
            
            {/* Campi della colonna */}
            <div className="space-y-3">
              {column.fields.map((field, fieldIndex) => {
                // Gestisci sia singoli campi che array di campi
                if (Array.isArray(field)) {
                  // Array di campi da affiancare (Nome e Tipo)
                  return (
                    <div key={fieldIndex} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {field.map(fieldName => {
                        const col = formVisibleColumnProps.find(c => c.field === fieldName);
                        if (!col) return null;
                        return (
                          <div key={fieldName}>
                            {renderFormInput(col)}
                          </div>
                        );
                      })}
                    </div>
                  );
                } else {
                  // Campo singolo
                  const col = formVisibleColumnProps.find(c => c.field === field);
                  if (!col) return null;
                  return (
                    <div key={fieldIndex}>
                      {renderFormInput(col)}
                    </div>
                  );
                }
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }, [formVisibleColumnProps, renderFormInput]);

  // NUOVO: Funzione per rendere il contenuto del form in base ai campi generali e parametri
  const renderFormContent = useCallback(() => {
    // Se abbiamo una configurazione di layout personalizzata, usala
    if (formLayoutConfig && formLayoutConfig.sections) {
      return (
        <div className="space-y-6">
          {formLayoutConfig.sections.map((section, index) => (
            <div key={index}>
              {/* Titolo della sezione */}
              {section.title && (
                <h4 className={`text-md font-semibold text-${themeColor}-800 dark:text-${themeColor}-200 mb-4 ${
                  index > 0 ? 'mt-6 pt-4 border-t border-gray-200 dark:border-gray-600' : ''
                }`}>
                  {section.title}
                </h4>
              )}
              
              {/* Contenuto della sezione */}
              {renderColumnSection(section, themeColor)}
            </div>
          ))}
        </div>
      );
    }

    // Layout predefinito se non c'è configurazione personalizzata
    return (
      <>
        {/* Prima Riga: Nome e Descrizione */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          {displayableGeneralFields.find(f => f.field === 'FAG_Nome') && renderFormInput(displayableGeneralFields.find(f => f.field === 'FAG_Nome'))}
          {displayableGeneralFields.find(f => f.field === 'FAG_Descrizione') && renderFormInput(displayableGeneralFields.find(f => f.field === 'FAG_Descrizione'))}
        </div>

        {/* Altri Campi Generali */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
          {displayableGeneralFields.filter(f => f.field !== 'FAG_Nome' && f.field !== 'FAG_Descrizione').map(col => (
            <div key={col.field}>
              {renderFormInput(col)}
            </div>
          ))}
        </div>

        {/* Sezione Parametri */}
        {parameterFields.length > 0 && (
          <>
            <h4 className="mt-6 mb-3 text-md font-semibold text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-600 pt-4">
              Parametri
            </h4>
            <div className="space-y-4">
              {parameterFields.map(paramSet => (
                <div key={`param-set-${paramSet.number}`} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 p-3 border dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800/30">
                  {renderFormInput(paramSet.nomeIn)}
                  {renderFormInput(paramSet.tipoIn)}
                  {renderFormInput(paramSet.nomeOut)}
                  {renderFormInput(paramSet.tipoOut)}
                </div>
              ))}
            </div>
          </>
        )}
      </>
    );
  }, [displayableGeneralFields, parameterFields, renderFormInput, formLayoutConfig, renderColumnSection, themeColor]);

  return (
    <div ref={ref} className={`mt-6 mb-8 p-4 md:p-6 bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700`}>
      <div className={`
        flex justify-between items-center px-6 py-4 mb-6 bg-gradient-to-r 
        from-${themeColor}-100 to-${themeColor}-200 
        dark:from-${themeColor}-900/40 dark:to-${themeColor}-800/40 
        border-b-2 border-${themeColor}-300 dark:border-${themeColor}-700
      `}>
        <h3 className={`text-lg font-bold text-${themeColor}-800 dark:text-${themeColor}-100 flex items-center`}>
          <FaPlus className="mr-3" />
          {`Nuovo ${creationNoun || 'Record'}`}
        </h3>
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100">
          <FaTimes className="h-5 w-5" />
        </button>
      </div>

      {/* Form Content */}
      <form onSubmit={handleSubmit} noValidate className="p-6">
        {/* MODIFICATO: Usa renderFormContent invece del layout fisso */}
        {renderFormContent()}
        
        {/* Buttons */}
        <div className="mt-6 flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition disabled:opacity-50 flex items-center"
          >
            <FaTimes className="mr-1.5 h-4 w-4" />
            Annulla
          </button>
          <button
            type="submit"
            disabled={isProcessing}
            className={`px-4 py-2 text-sm bg-${themeColor}-600 text-white rounded-md hover:bg-${themeColor}-700 transition disabled:opacity-50 flex items-center`}
          >
            {isProcessing ? (
              <FaSpinner className="animate-spin mr-1.5 h-4 w-4" />
            ) : (
              <FaPlus className="mr-1.5 h-4 w-4" />
            )}
            Crea {creationNoun || 'Record'}
          </button>
        </div>
      </form>
    </div>
  );
});

NewRecordForm.displayName = 'NewRecordForm';
export default NewRecordForm;
